/* eslint-env browser */
// One driver's call-centre notes (owner feedback 29 Oct, item 5: adding a
// note while on the phone with a driver took about six clicks). The note box
// is on top and ready to type, the category is one tap on a chip (still
// required), Save or Ctrl / Cmd + Enter saves, and the communication history
// is right under it, newest first. Shown in the Drivers list's note dialog
// (components/QuickDriverNote.js).
//
// Reads: the driver's notes are read only while this panel is on screen -
// fetchDriverRemarks when it opens, stopFetchingDriverRemarks when it closes
// (one indexed query on driver_remarks by driverId). The store keeps ONE
// remarks list for whichever driver was opened last, so the rows are matched
// to this driver: another driver's notes never show while this one's load.
// Writes: addDriverRemark, unchanged. For every category but Other it also
// stamps the driver's lastContactDate / Type / Agent on /users; that stamp is
// mirrored into the drivers already loaded (patchUsersCacheUpsert: Redux and
// this browser's users cache, no read), so the last contact and the Notes
// filter are right without fetching the users again.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { tokens as c, iconHues } from '../theme';
import { showToast, InlineMessage } from './feedback';
import { onPageStateReset } from './modern/pageState';
import { formatAddisDateTime, formatAddisTime, addisDateStamp } from '../common/exportUtils';

export const CALL_CATEGORIES = ['Compliance', 'Booking', 'Credit', 'SOS', 'Complain', 'Other'];

// One hue per category, dark text on its own tint (the old chips put white
// text on orange and green, under 3:1). Same families as before: blue, green,
// amber, red, violet, grey.
const CATEGORY_HUE = {
  Compliance: 'info',
  Booking: 'success',
  Credit: 'amber',
  SOS: 'danger',
  Complain: 'violet',
  Other: 'neutral',
};
export const categoryHue = (category) => iconHues[CATEGORY_HUE[category] || 'neutral'] || iconHues.neutral;
// The category names are translated with the existing keys (compliance,
// booking, credit, sos, complain, other).
export const categoryLabel = (category, t) =>
  category ? t(String(category).toLowerCase(), { defaultValue: String(category) }) : '';

// Who may add a note: the roles the database rules let write driver_remarks.
export const NOTE_ROLES = ['admin', 'callcenteragent', 'callcentersupervisor', 'finance', 'marketing'];
// Who may also write the driver's lastContact* fields on /users (marketing
// may add notes but not touch /users, so its stamp is never mirrored).
const LAST_CONTACT_ROLES = ['admin', 'callcenteragent', 'callcentersupervisor', 'finance'];

/** "Today, 3:40 pm" or "27 Oct 2026, 11:38 am", in Addis Ababa time. */
export const noteTime = (value, t) => {
  if (!value) return '';
  const day = addisDateStamp(value);
  if (!day) return '';
  return day === addisDateStamp(Date.now())
    ? `${t('today', { defaultValue: 'Today' })}, ${formatAddisTime(value)}`
    : formatAddisDateTime(value);
};

// "Hana Tesfaye", else the e-mail (agentName was written as
// firstName + " " + lastName, so a profile without names left "undefined undefined").
export const agentOf = (remark) => {
  const name = String(remark.agentName || '')
    .replace(/\b(undefined|null)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return name || remark.agentEmail || '';
};

// Unsaved text stays per driver for this visit, so closing the dialog by
// mistake (a click beside it, Esc) loses nothing; it goes at sign-out with
// the lists' kept filters, so the next person on a shared computer starts clean.
const drafts = new Map();
onPageStateReset(() => drafts.clear());

const IS_MAC = (() => {
  try {
    return /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent || '');
  } catch (e) {
    return false;
  }
})();

const sectionTitle = { fontSize: 15, fontWeight: 700, lineHeight: 1.4, color: c.INK };

// One category as a 44-px chip in a radio group: a dot in its colour, a
// check and its colour when chosen (never colour alone).
function CategoryChip({ value, label, selected, tabbable, onSelect, onKeyDown, chipRef }) {
  const hue = categoryHue(value);
  return (
    <Chip
      ref={chipRef}
      clickable
      role="radio"
      aria-checked={selected ? 'true' : 'false'}
      tabIndex={tabbable ? 0 : -1}
      onClick={() => onSelect(value)}
      onKeyDown={onKeyDown}
      icon={
        selected ? (
          <CheckRoundedIcon />
        ) : (
          <Box component="span" aria-hidden="true" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: hue.fg, flex: 'none' }} />
        )
      }
      label={label}
      sx={{
        height: 44,
        borderRadius: '22px',
        px: 0.75,
        fontSize: 15,
        fontWeight: 600,
        bgcolor: selected ? hue.bg : c.SURFACE,
        color: selected ? hue.fg : c.INK,
        border: `${selected ? 1.5 : 1}px solid ${selected ? hue.fg : c.OUTLINE_STRONG}`,
        '& .MuiChip-icon': { color: hue.fg, fontSize: 18, ml: 0.75, mr: -0.25 },
        '& .MuiChip-label': { px: 1 },
        '&.MuiChip-clickable:hover': { bgcolor: selected ? hue.bg : c.SURFACE_ALT },
        '&.Mui-focusVisible': { outline: `2px solid ${c.ACCENT}`, outlineOffset: 2 },
      }}
    />
  );
}

function NoteItem({ remark, t }) {
  const hue = categoryHue(remark.callType);
  const agent = agentOf(remark);
  const when = noteTime(remark.timestamp || remark.createdAt, t);
  return (
    <Box
      component="li"
      sx={{
        listStyle: 'none',
        p: { xs: 1.5, sm: 2 },
        border: `1px solid ${c.OUTLINE}`,
        borderRadius: '12px',
        bgcolor: c.SURFACE,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5 }}>
        {remark.callType ? (
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              height: 24,
              px: 1,
              borderRadius: '12px',
              bgcolor: hue.bg,
              color: hue.fg,
              fontSize: 13,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {categoryLabel(remark.callType, t)}
          </Box>
        ) : null}
        {agent ? (
          <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, color: c.INK, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
            {agent}
          </Typography>
        ) : null}
        {when ? (
          <Typography component="span" sx={{ fontSize: 13, color: c.TEXT_3, lineHeight: 1.45, whiteSpace: 'nowrap' }}>
            {when}
          </Typography>
        ) : null}
      </Box>
      <Typography sx={{ fontSize: 15, lineHeight: 1.55, color: c.INK, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {remark.remark}
      </Typography>
    </Box>
  );
}

/**
 * The notes of one driver: the note form (for NOTE_ROLES) and the history.
 * Give it key={driverId} when the driver can change while it is mounted.
 *
 *   <DriverRemarks driverId={row.id} driverData={row} autoFocus />
 */
export default function DriverRemarks({ driverId, driverData, autoFocus = false }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const reduxStore = useStore();
  const role = useSelector((state) => (state.auth && state.auth.profile ? state.auth.profile.usertype : null));
  const agentEmail = useSelector((state) => (state.auth && state.auth.profile ? state.auth.profile.email : ''));
  const remarksData = useSelector((state) => state.driverremarksdata) || {};
  const canAdd = NOTE_ROLES.includes(role);

  const kept = (driverId && drafts.get(driverId)) || { text: '', category: '' };
  const [text, setText] = useState(kept.text);
  const [category, setCategory] = useState(kept.category);
  const [needCategory, setNeedCategory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState('');
  const [requested, setRequested] = useState(false);
  const boxRef = useRef(null);
  const chipRefs = useRef([]);
  const mounted = useRef(true);

  // The driver's notes are listened to while this is shown, and released
  // when it closes.
  useEffect(() => {
    mounted.current = true;
    if (!driverId) return undefined;
    dispatch(api.fetchDriverRemarks(driverId));
    setRequested(true);
    return () => {
      mounted.current = false;
      dispatch(api.stopFetchingDriverRemarks(driverId));
    };
  }, [driverId, dispatch]);

  // A kept draft opens with the caret at its end.
  useEffect(() => {
    const el = boxRef.current;
    if (autoFocus && el && kept.text && typeof el.setSelectionRange === 'function') {
      el.setSelectionRange(kept.text.length, kept.text.length);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- once, as the dialog opens (the draft it opened with)

  useEffect(() => {
    if (!driverId) return;
    if (text || category) drafts.set(driverId, { text, category });
    else drafts.delete(driverId);
  }, [driverId, text, category]);

  // addDriverRemark returns nothing: its result is the store's addLoading /
  // addError pair. It is watched through the store itself, so the outcome is
  // handled even when the dialog was closed while the note was on its way.
  // (The pair is shared: two notes saved at the same moment from two
  // dialogs settle on the first answer. One dialog saves one note at a time.)
  const watchSave = (sent) => {
    const unsubscribe = reduxStore.subscribe(() => {
      const now = reduxStore.getState().driverremarksdata || {};
      if (now.addLoading) return;
      unsubscribe();
      if (now.addError) {
        // Nothing is lost: the note goes back in the box (in front of
        // anything typed since), or into the kept draft when closed.
        if (mounted.current) {
          setText((typed) => (typed.trim() ? `${sent.note}\n${typed}` : sent.note));
          setCategory((chosen) => chosen || sent.category);
          setFailed(String(now.addError));
        } else {
          const draft = drafts.get(sent.driverId) || { text: '', category: '' };
          drafts.set(sent.driverId, {
            text: draft.text.trim() ? `${sent.note}\n${draft.text}` : sent.note,
            category: draft.category || sent.category,
          });
          showToast.error(t('driver_note_save_failed', { defaultValue: 'Could not save the note. Check the connection and try again.' }));
        }
      } else {
        if (sent.category !== 'Other' && sent.mirror && typeof api.patchUsersCacheUpsert === 'function') {
          Promise.resolve(
            api.patchUsersCacheUpsert(sent.driverId, {
              lastContactDate: sent.at,
              lastContactType: sent.category,
              lastContactAgent: sent.agentEmail,
            })
          ).catch(() => {});
        }
        showToast.success(t('driver_note_saved', { defaultValue: 'Note saved' }));
      }
      if (mounted.current) setSaving(false);
    });
  };

  const remarks = useMemo(
    () => (Array.isArray(remarksData.remarks) ? remarksData.remarks.filter((r) => r && r.driverId === driverId) : []),
    [remarksData.remarks, driverId]
  );
  const listLoading = (!requested || !!remarksData.loading) && remarks.length === 0;
  const listError = remarksData.error && !remarks.length ? String(remarksData.error) : '';

  const focusBox = () => {
    const el = boxRef.current;
    if (el && typeof el.focus === 'function') el.focus();
  };
  const focusChip = (index) => {
    const el = chipRefs.current[index];
    if (el && typeof el.focus === 'function') el.focus();
  };

  const choose = (value) => {
    setCategory(value);
    setNeedCategory(false);
  };

  const save = () => {
    if (!canAdd || saving) return;
    // One note at a time across dialogs: the store has a single saving flag,
    // so a second save while another note is still sending could take that
    // note's answer and lose its own (review, 29 Oct).
    const pending = reduxStore.getState().driverremarksdata;
    if (pending && pending.addLoading) {
      setFailed(t('driver_note_wait', { defaultValue: 'The previous note is still sending. Try again in a moment.' }));
      return;
    }
    const note = text.trim();
    if (!note) {
      focusBox();
      return;
    }
    if (!category) {
      setNeedCategory(true);
      focusChip(0);
      return;
    }
    setFailed('');
    const sent = {
      driverId,
      note,
      category,
      at: new Date().toISOString(),
      agentEmail: agentEmail || '',
      mirror: LAST_CONTACT_ROLES.includes(role),
    };
    dispatch(
      api.addDriverRemark({
        driverId,
        driverName: `${(driverData && driverData.firstName) || ''} ${(driverData && driverData.lastName) || ''}`,
        driverMobile: (driverData && driverData.mobile) || '',
        remark: note,
        callType: category,
      })
    );
    const state = reduxStore.getState();
    if (!(state.driverremarksdata && state.driverremarksdata.addLoading)) {
      setFailed(t('driver_note_not_sent', { defaultValue: 'The note was not sent. Try again.' }));
      return;
    }
    watchSave(sent);
    // The box clears at once (the note shows at the top of the history as
    // soon as it is written) and is ready for the next one; a failed save
    // puts it back.
    setSaving(true);
    setText('');
    setCategory('');
    setNeedCategory(false);
    drafts.delete(driverId);
    focusBox();
  };

  // Ctrl + Enter (Cmd + Enter on a Mac) saves from the box or the chips.
  const onFormKeyDown = (event) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      save();
    }
  };

  // The chips are one radio group: one Tab stop, arrows move and choose.
  const selectedIndex = CALL_CATEGORIES.indexOf(category);
  const onChipKeyDown = (index) => (event) => {
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const next = (index + step + CALL_CATEGORIES.length) % CALL_CATEGORIES.length;
    choose(CALL_CATEGORIES[next]);
    focusChip(next);
  };

  const boxId = `driver-note-${driverId || 'x'}`;
  const groupLabelId = `${boxId}-category`;
  const errorId = `${boxId}-category-error`;
  const shortcut = t('driver_note_shortcut', {
    keys: IS_MAC ? '⌘ + Enter' : 'Ctrl + Enter',
    defaultValue: '{{keys}} saves the note',
  });
  const busy = !!saving;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {canAdd ? (
        <Box component="form" noValidate onSubmit={(e) => { e.preventDefault(); save(); }} onKeyDown={onFormKeyDown} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box>
            <Typography component="label" htmlFor={boxId} sx={{ ...sectionTitle, display: 'block', mb: 0.75 }}>
              {t('driver_note_new', { defaultValue: 'New note' })}
            </Typography>
            <TextField
              id={boxId}
              inputRef={boxRef}
              fullWidth
              multiline
              minRows={3}
              maxRows={10}
              autoFocus={autoFocus}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                if (failed) setFailed('');
              }}
              placeholder={t('driver_note_placeholder', { defaultValue: 'What did you talk about?' })}
              inputProps={{ spellCheck: true }}
              sx={{ '& .MuiInputBase-root': { fontSize: 15, lineHeight: 1.5, alignItems: 'flex-start' } }}
            />
          </Box>
          <Box>
            <Typography id={groupLabelId} component="div" sx={{ fontSize: 14, fontWeight: 600, color: c.INK_2, lineHeight: 1.45, mb: 0.75 }}>
              {t('call_category', { defaultValue: 'Call category' })}
            </Typography>
            <Box
              role="radiogroup"
              aria-labelledby={groupLabelId}
              aria-required="true"
              aria-invalid={needCategory ? 'true' : 'false'}
              aria-describedby={needCategory ? errorId : undefined}
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
            >
              {CALL_CATEGORIES.map((value, index) => (
                <CategoryChip
                  key={value}
                  value={value}
                  label={categoryLabel(value, t)}
                  selected={category === value}
                  tabbable={selectedIndex === -1 ? index === 0 : selectedIndex === index}
                  onSelect={choose}
                  onKeyDown={onChipKeyDown(index)}
                  chipRef={(el) => {
                    chipRefs.current[index] = el;
                  }}
                />
              ))}
            </Box>
            {needCategory ? (
              <Box
                id={errorId}
                role="alert"
                sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.5, color: c.DANGER_TEXT, fontSize: 13.5, fontWeight: 600, lineHeight: 1.45 }}
              >
                <ErrorOutlineRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} />
                {t('driver_note_choose_category', { defaultValue: 'Choose a category for the note.' })}
              </Box>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1.5 }}>
            <Typography component="span" sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 13, color: c.TEXT_3, lineHeight: 1.45, mr: 'auto' }}>
              {shortcut}
            </Typography>
            <Button
              type="submit"
              variant="contained"
              disabled={busy}
              startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <CheckRoundedIcon />}
              sx={{ minHeight: 44, minWidth: 140, width: { xs: '100%', sm: 'auto' } }}
            >
              {busy ? t('driver_note_saving', { defaultValue: 'Saving…' }) : t('driver_note_save', { defaultValue: 'Save note' })}
            </Button>
          </Box>
          {failed ? (
            <InlineMessage kind="error" title={t('driver_note_save_failed', { defaultValue: 'Could not save the note. Check the connection and try again.' })}>
              {failed}
            </InlineMessage>
          ) : null}
        </Box>
      ) : null}

      {canAdd ? <Divider /> : null}

      <Box component="section" aria-labelledby={`${boxId}-history`} sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1 }}>
          <Typography id={`${boxId}-history`} component="h3" sx={sectionTitle}>
            {t('communication_history', { defaultValue: 'Communication history' })}
          </Typography>
          {!listLoading && remarks.length ? (
            <Typography component="span" sx={{ fontSize: 14, color: c.TEXT_3, fontVariantNumeric: 'tabular-nums' }}>
              {t('driver_note_count', {
                count: remarks.length,
                defaultValue_one: '{{count}} note',
                defaultValue_other: '{{count}} notes',
                defaultValue: '{{count}} notes',
              })}
            </Typography>
          ) : null}
        </Box>
        {listLoading && !listError ? (
          <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 2, color: c.INK_2, fontSize: 14 }}>
            <CircularProgress size={20} />
            {t('driver_note_loading', { defaultValue: 'Loading notes…' })}
          </Box>
        ) : remarks.length ? (
          <Box component="ul" sx={{ m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {remarks.map((remark) => (
              <NoteItem key={remark.id} remark={remark} t={t} />
            ))}
          </Box>
        ) : listError ? null : (
          <Typography sx={{ py: 2, fontSize: 14, color: c.TEXT_3, lineHeight: 1.5 }}>
            {t('driver_note_none', { defaultValue: 'No notes for this driver yet.' })}
          </Typography>
        )}
        {listError ? (
          <InlineMessage kind="error" title={t('driver_note_load_failed', { defaultValue: 'Could not load the notes.' })}>
            {listError}
          </InlineMessage>
        ) : null}
      </Box>
    </Box>
  );
}
